// =============================================================================
// src/features/analytics/analytics-filter-markup.ts - Rail contextual Analytics.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import {
  OMAR_ANALYTICS_FILTER_SETS,
  type OmarAnalyticsFilterOption,
  type OmarAnalyticsPlatformCount,
} from './analytics-data';
import { ANALYTICS_FEED_SCOPES, ANALYTICS_SCOPE_LABELS } from '../../data/analytics-scope-where';
import { analyticsDemoTagMarkup } from './analytics-demo-tag';
import { analyticsLiveSource, getAnalyticsPlatformCounts } from './analytics-live-store';
import { getAnalyticsViewState } from './analytics-view-state';

/** Renderiza o rail direito com filtros por modulo. */
export function buildAnalyticsFilterRailMarkup(): string {
  return `<aside class="u-analytics-filter-rail" aria-label="Filtros Analytics">
    <button class="u-analytics-collapse-btn u-analytics-collapse-right" type="button" data-analytics-toggle-panel="right" aria-label="Recolher filtros"><i class="ti ti-layout-sidebar-right-collapse"></i></button>
    <button class="u-analytics-filter-close" type="button" data-analytics-filter-drawer-close aria-label="Fechar filtros"><i class="ti ti-x"></i></button>
    <div class="u-analytics-filter-body">
      ${analyticsMentionsFilterMarkup()}${analyticsDestaquesFilterMarkup()}${analyticsAnalysisFilterMarkup()}${analyticsDigestFilterMarkup()}${analyticsInfluencersFilterMarkup()}${analyticsSitesFilterMarkup()}${analyticsComparisonFilterMarkup()}${analyticsReportsFilterMarkup()}${analyticsSecondaryFilterPanelsMarkup()}
    </div>
  </aside>`;
}

function analyticsMentionsFilterMarkup(): string {
  return `<div class="u-analytics-filter-panel is-active" data-analytics-filter-panel="mentions">
    ${analyticsPlatformFilterMarkup()}${analyticsRelevanceFilterMarkup()}${analyticsRelationFilterMarkup()}${analyticsOptionGroupMarkup('Sentimento', 'sentiment', OMAR_ANALYTICS_FILTER_SETS.sentiments, 'all')}
    ${analyticsOptionGroupMarkup('Emocao', 'local-chip', OMAR_ANALYTICS_FILTER_SETS.emotions)}${analyticsOptionGroupMarkup('Idioma', 'local-chip', OMAR_ANALYTICS_FILTER_SETS.languages)}
  </div>`;
}

// 2026-09-20: painel VAZIO, como Relatorios e Configuracoes. Destaques le o
// mesmo recorte servido que Mencoes, e repetir os chips de canal/escopo aqui
// duplicaria os data-attributes no DOM — dois grupos com o mesmo valor, ligados
// duas vezes pelo controller. O recorte se troca na aba Mencoes e vale para as
// duas telas.
function analyticsDestaquesFilterMarkup(): string {
  return `<div class="u-analytics-filter-panel" data-analytics-filter-panel="destaques" hidden></div>`;
}

function analyticsAnalysisFilterMarkup(): string {
  return `<div class="u-analytics-filter-panel" data-analytics-filter-panel="analysis" hidden>
    ${analyticsPlatformFilterMarkup()}${analyticsPerformanceFilterMarkup()}${analyticsSavedFiltersMarkup()}
  </div>`;
}

function analyticsDigestFilterMarkup(): string {
  return `<div class="u-analytics-filter-panel" data-analytics-filter-panel="digest" hidden>
    ${analyticsOptionGroupMarkup('Foco do resumo', 'local-chip', [{ optionValue: 'territory', label: 'Territorio' }, { optionValue: 'risk', label: 'Risco' }])}
    ${analyticsOptionGroupMarkup('Idioma', 'local-chip', OMAR_ANALYTICS_FILTER_SETS.languages)}${analyticsSavedFiltersMarkup()}
  </div>`;
}

function analyticsInfluencersFilterMarkup(): string {
  return `<div class="u-analytics-filter-panel" data-analytics-filter-panel="influencers" hidden>
    ${analyticsOptionGroupMarkup('Performance', 'local-chip', OMAR_ANALYTICS_FILTER_SETS.performanceBands)}
    ${analyticsOptionGroupMarkup('Sentimento', 'sentiment', OMAR_ANALYTICS_FILTER_SETS.sentiments, 'all')}
    <div class="u-analytics-filter-title">Seguidores</div><div class="u-analytics-range"><span>0</span><i></i><span>10 mi</span></div>
  </div>`;
}

function analyticsSitesFilterMarkup(): string {
  return `<div class="u-analytics-filter-panel" data-analytics-filter-panel="sites" hidden>
    ${analyticsOptionGroupMarkup('Tipo de site', 'site-kind', OMAR_ANALYTICS_FILTER_SETS.siteKinds, 'all')}
    ${analyticsOptionGroupMarkup('Sentimento', 'sentiment', OMAR_ANALYTICS_FILTER_SETS.sentiments, 'all')}
    <div class="u-analytics-filter-title">Visitas</div><div class="u-analytics-range"><span>0</span><i></i><span>1 bi</span></div>
  </div>`;
}

function analyticsComparisonFilterMarkup(): string {
  return `<div class="u-analytics-filter-panel" data-analytics-filter-panel="comparison" hidden>
    ${analyticsPlatformFilterMarkup()}${analyticsOptionGroupMarkup('Comparar por', 'local-chip', OMAR_ANALYTICS_FILTER_SETS.performanceBands)}
    <div class="u-analytics-filter-title">Recorte</div><div class="u-analytics-saved-filter">Omar Aziz x campo</div><div class="u-analytics-saved-filter">Manaus critica</div>
  </div>`;
}

// Filtros de Relatorios e Configuracoes RETIRADOS (ordens do usuario
// 2026-07-25): paineis vazios, nenhum chip mock no rail dessas abas. Para
// religar: devolver analyticsSavedFiltersMarkup + grupo de opcoes.
function analyticsReportsFilterMarkup(): string {
  return `<div class="u-analytics-filter-panel" data-analytics-filter-panel="reports" hidden></div>`;
}

function analyticsSecondaryFilterPanelsMarkup(): string {
  return ['settings', 'emails', 'assistant', 'team']
    .map((moduleName) => {
      // Configuracoes SEM filtro nenhum no rail (ordem do usuario 2026-07-25:
      // "retirou o filtro da aba de configuracoes") — painel vazio.
      const panelContent = moduleName === 'settings'
        ? ''
        : `${analyticsSavedFiltersMarkup(true)}${analyticsInertChipGroupMarkup('Estado', [{ optionValue: 'active', label: 'Ativo' }, { optionValue: 'review', label: 'Revisar' }])}`;
      return `<div class="u-analytics-filter-panel" data-analytics-filter-panel="${moduleName}" hidden>${panelContent}</div>`;
    })
    .join('');
}

/** Grupo de chips inerte: sem data-attribute (nada para o controller ligar). */
function analyticsInertChipGroupMarkup(groupLabel: string, filterOptions: readonly OmarAnalyticsFilterOption[]): string {
  return `<div class="u-analytics-filter-title">${escapeHtml(groupLabel)}</div><div class="u-analytics-filter-group" role="group" aria-label="${escapeHtml(groupLabel)}">
    ${filterOptions.map((filterOption) => `<button type="button" disabled title="Disponivel na proxima fase">${escapeHtml(filterOption.label)}</button>`).join('')}
  </div>`;
}

/** Escopo Amazonas (migrations 017/018/025) — só no live. O que está FORA de escopo
 * (Michelle Bolsonaro, notícia do Ceará, Don Omar) já não é servido pelo backend;
 * aqui o operador escolhe o RECORTE que o servidor devolve (2026-09-02): "Nucleo"
 * = cenário político do AM (padrão), "Omar" = só o que tem relação com o VIP,
 * "Contexto" = AM sem carga política (ZFM, Arena da Amazônia, esporte). O toggle
 * "Mostrar reacoes" re-inclui comentários curtos ("Volta Omar 5️⃣5️⃣") — os
 * vazios ("👏👏👏") nunca voltam. */
function analyticsRelevanceFilterMarkup(): string {
  if (analyticsLiveSource() !== 'live') return '';
  const viewState = getAnalyticsViewState();
  const scopeButtons = ANALYTICS_FEED_SCOPES
    .map((scope) => `<button type="button" class="${scope === viewState.scope ? 'is-active' : ''}" aria-pressed="${scope === viewState.scope ? 'true' : 'false'}" data-analytics-relevance-filter="${scope}">${ANALYTICS_SCOPE_LABELS[scope]}</button>`)
    .join('');
  const reactionsActive = viewState.showReactions ? ' is-active' : '';
  return `<div class="u-analytics-filter-title">Escopo</div><div class="u-analytics-filter-group" role="group" aria-label="Escopo">
    ${scopeButtons}
  </div>
    <div class="u-analytics-filter-group" role="group" aria-label="Comentarios">
    <button type="button" class="u-analytics-toggle-chip${reactionsActive}" aria-pressed="${viewState.showReactions ? 'true' : 'false'}" data-analytics-reactions-toggle title="Inclui comentarios curtos de apoio (reacao); vazios nunca entram">Mostrar reacoes</button>
  </div>`;
}

/** Tipo da relação ator↔conteúdo (016) — só no live (o mock não tem matches).
 * Publicação = postado pelo monitorado; Marcação = @ no texto; Menção = citado. */
function analyticsRelationFilterMarkup(): string {
  if (analyticsLiveSource() !== 'live') return '';
  const relationOptions: readonly (readonly [string, string])[] = [
    ['all', 'Todos'], ['publicacao', 'Publicações'], ['marcacao', 'Marcações'], ['mencao', 'Menções'],
  ];
  const relationButtons = relationOptions
    .map(([relationValue, relationLabel]) => `<button type="button" class="${relationValue === 'all' ? 'is-active' : ''}" data-analytics-relation-filter="${relationValue}">${relationLabel}</button>`)
    .join('');
  return `<div class="u-analytics-filter-title">Tipo de conteudo</div><div class="u-analytics-filter-group" role="group" aria-label="Tipo de conteudo">
    ${relationButtons}
  </div>`;
}

function analyticsPlatformFilterMarkup(): string {
  return `<div class="u-analytics-filter-title">Canais monitorados</div>
    <div class="u-analytics-platforms">${getAnalyticsPlatformCounts().map(analyticsPlatformMarkup).join('')}</div>`;
}

function analyticsPlatformMarkup(platformCount: OmarAnalyticsPlatformCount): string {
  const activeClass = platformCount.platform === 'Todos' ? ' is-active' : '';
  // Canal sem coleta no recorte (live): botao desabilitado em vez de filtrar
  // para o vazio e parecer quebrado (feedback do usuario 2026-07-25).
  const liveNoData = analyticsLiveSource() === 'live' && platformCount.count === 0 && platformCount.platform !== 'Todos';
  const disabledAttrs = liveNoData ? ' disabled title="Sem coleta neste canal no recorte"' : '';
  return `<button type="button" class="u-analytics-platform${activeClass}" data-analytics-platform-filter="${escapeHtml(platformCount.platform)}"${disabledAttrs}>
    <i class="ti ${platformCount.iconName}" aria-hidden="true"></i><span>${escapeHtml(platformCount.platform)}</span><b>${platformCount.count}</b>
  </button>`;
}

function analyticsPerformanceFilterMarkup(): string {
  return `<div class="u-analytics-filter-title">Desempenho</div>
    <div class="u-analytics-range"><span>0</span><i></i><span>10</span></div>
    ${analyticsOptionGroupMarkup('Sentimento', 'sentiment', OMAR_ANALYTICS_FILTER_SETS.sentiments, 'all')}`;
}

// Grupos OCULTOS POR HORA no rail (pedido do usuario 2026-07-25): Sentimento e
// Emocao nao existem na coleta viva (F3.1) e Idioma nao tem dado real — melhor
// sumir do que exibir grupo-demonstracao ao lado de filtros reais. Vale para
// TODOS os paineis do rail (mencoes, analises, influenciadores, sites, digest).
// Para religar: remover o rotulo desta lista (a tag demonstracao + disabled do
// caminho live continuam implementadas logo abaixo).
const ANALYTICS_HIDDEN_FILTER_GROUPS: ReadonlySet<string> = new Set(['Sentimento', 'Emocao', 'Idioma']);

function analyticsOptionGroupMarkup(
  groupLabel: string,
  groupName: 'sentiment' | 'site-kind' | 'local-chip',
  filterOptions: readonly OmarAnalyticsFilterOption[],
  defaultValue?: string,
): string {
  if (ANALYTICS_HIDDEN_FILTER_GROUPS.has(groupLabel)) return '';
  // Sentimento/emocao nao existem na coleta viva (F3.1): quando live, o grupo
  // ganha a tag demonstracao e as opcoes (menos o default) ficam disabled em
  // vez de filtrar para o vazio e parecer quebrado.
  const groupTag = analyticsFilterGroupNeedsSentiment(groupLabel) ? analyticsDemoTagMarkup() : '';
  return `<div class="u-analytics-filter-title">${escapeHtml(groupLabel)}${groupTag}</div><div class="u-analytics-filter-group" role="group" aria-label="${escapeHtml(groupLabel)}">
    ${filterOptions.map((filterOption) => analyticsOptionButtonMarkup(groupName, groupLabel, filterOption, defaultValue)).join('')}
  </div>`;
}

function analyticsFilterGroupNeedsSentiment(groupLabel: string): boolean {
  return groupLabel === 'Sentimento' || groupLabel === 'Emocao';
}

function analyticsOptionButtonMarkup(
  groupName: 'sentiment' | 'site-kind' | 'local-chip',
  groupLabel: string,
  filterOption: OmarAnalyticsFilterOption,
  defaultValue?: string,
): string {
  const activeClass = filterOption.optionValue === defaultValue ? ' is-active' : '';
  const dataAttribute = analyticsOptionDataAttribute(groupName, filterOption.optionValue);
  const liveDisabled = analyticsFilterGroupNeedsSentiment(groupLabel)
    && analyticsLiveSource() === 'live'
    && filterOption.optionValue !== defaultValue;
  return `<button type="button" class="${activeClass.trim()}" ${dataAttribute}${liveDisabled ? ' disabled' : ''}>${escapeHtml(filterOption.label)}</button>`;
}

function analyticsOptionDataAttribute(groupName: 'sentiment' | 'site-kind' | 'local-chip', optionValue: string): string {
  if (groupName === 'sentiment') return `data-analytics-sentiment-filter="${escapeHtml(optionValue)}"`;
  if (groupName === 'site-kind') return `data-analytics-site-kind-filter="${escapeHtml(optionValue)}"`;
  return 'data-analytics-local-chip';
}

function analyticsSavedFiltersMarkup(inert = false): string {
  const inertClass = inert ? ' is-disabled' : '';
  return `<div class="u-analytics-filter-title">Filtros salvos</div>
    ${OMAR_ANALYTICS_FILTER_SETS.savedFilters.map((savedFilter) => `<div class="u-analytics-saved-filter${inertClass}">${escapeHtml(savedFilter.label)}</div>`).join('')}`;
}
